import React, { useRef } from 'react';

import FilterBar from '../components/FilterBar';
import HomeRecipeGrid from '../components/home/HomeRecipeGrid';
import HomeRecipeList from '../components/home/HomeRecipeList';
import { Box, EmptyState, Flex, ListGroup, Spinner } from '../components/ui';

import { useDisplayMode } from '../context/DisplayModeContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';

import { useRecipes } from '../hooks/recipes/useRecipes';
import useInfiniteScroll from '../hooks/ui/useInfiniteScroll';
import usePullToRefresh from '../hooks/ui/usePullToRefresh';
import useAutoRefresh from '../hooks/ui/useAutoRefresh';
import useHomePageController from '../hooks/home/useHomePageController';

const RECIPES_PER_PAGE = 10;

interface SortField {
  key: string;
  label: string;
}

interface SortDirection {
  key: 'asc' | 'desc';
  label: string;
  icon: string;
}

const SORT_FIELDS: SortField[] = [
  { key: 'created_at', label: 'Date de création' },
  { key: 'title', label: 'Titre' },
];

const SORT_DIRECTIONS: SortDirection[] = [
  { key: 'asc', label: 'Croissant', icon: '↑' },
  { key: 'desc', label: 'Décroissant', icon: '↓' },
];

const DAILY_SELECTION_LABEL = 'La sélection du jour';

interface HomeProps {
  navbarHeight: number;
}

const Home: React.FC<HomeProps> = ({ navbarHeight }) => {
  const { displayMode, toggleDisplayMode } = useDisplayMode();
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);

  const {
    allRecipes,
    dailySelectionRecipe,
    loading,
    loadingMore,
    hasMore,
    isRefreshing,
    loadMore,
    refreshRecipes,
    isLoadingTags,
    isLoadingAuthors,
    allTags,
    selectedTagIds,
    handleTagChange,
    allAuthors,
    selectedAuthorIds,
    handleAuthorChange,
    clearFilters,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = useRecipes();

  const { addFavorite, removeFavorite, isFavorite, favoritePendingIds } = useFavorites();
  const { showToast } = useToast();

  const lastRecipeElementRef = useInfiniteScroll<HTMLElement>({
    enabled: !loadingMore,
    hasMore,
    onLoadMore: loadMore,
  });

  const { pullDelta, threshold: pullThreshold } = usePullToRefresh({
    isRefreshing,
    onRefresh: refreshRecipes,
  });

  useAutoRefresh(refreshRecipes, 60 * 1000);

  const {
    displayedRecipes,
    showDailySelection,
    showEmptyState,
    handleFavoriteToggle,
    filterBarComponentRef,
    isFilterBarOpen,
    onClearFilters,
    handleFilterBarToggle,
    handleSortFieldChange,
    handleSortDirectionChange,
  } = useHomePageController({
    displayMode,
    gridScrollRef,
    listScrollRef,
    allRecipes,
    dailySelectionRecipe,
    hasMore,
    isRefreshing,
    loading,
    isLoadingTags,
    isLoadingAuthors,
    selectedTagIds,
    selectedAuthorIds,
    sortField,
    sortDirection,
    clearFilters,
    setSortField,
    setSortDirection,
    showToast,
    isFavorite,
    addFavorite,
    removeFavorite,
  });

  return (
    <Flex direction="column" fullHeight>
      <FilterBar
        ref={filterBarComponentRef}
        allTags={allTags}
        allAuthors={allAuthors}
        selectedTagIds={selectedTagIds}
        selectedAuthorIds={selectedAuthorIds}
        onTagChange={handleTagChange}
        onAuthorChange={handleAuthorChange}
        onClearFilters={onClearFilters}
        isOpen={isFilterBarOpen}
        onToggle={handleFilterBarToggle}
        navbarHeight={navbarHeight}
        sortFields={SORT_FIELDS}
        selectedSortField={sortField}
        sortDirections={SORT_DIRECTIONS}
        selectedSortDirection={sortDirection}
        onSortFieldChange={handleSortFieldChange}
        onSortDirectionChange={handleSortDirectionChange}
        displayMode={displayMode}
        onToggleDisplayMode={toggleDisplayMode}
      />

      {isRefreshing && (
        <Box
          style={{
            transform: `translateY(${pullDelta}px)`,
            textAlign: 'center',
            padding: '10px',
            opacity: pullDelta / pullThreshold,
          }}
        >
          <Spinner label="Chargement..." />
        </Box>
      )}

      {showEmptyState ? (
        <EmptyState variant="warning" marginTop={5}>Aucune recette trouvée.</EmptyState>
      ) : displayMode === 'grid' ? (
        <Box ref={gridScrollRef} overflowY="scroll" className="instagram-feed scroll-snap-enabled">
          <HomeRecipeGrid
            recipes={displayedRecipes}
            showDailySelection={showDailySelection}
            loading={loading}
            loadingMore={loadingMore}
            recipesPerPage={RECIPES_PER_PAGE}
            dailySelectionLabel={DAILY_SELECTION_LABEL}
            favoritePendingIds={favoritePendingIds}
            isFavorite={isFavorite}
            onFavoriteToggle={(recipe) => {
              void handleFavoriteToggle(recipe);
            }}
            lastRecipeElementRef={lastRecipeElementRef}
          />
        </Box>
      ) : (
        <ListGroup
          ref={listScrollRef}
          container="fluid"
          overflowY="scroll"
          className="scroll-snap-enabled"
          paddingX={0}
        >
          <HomeRecipeList
            recipes={displayedRecipes}
            showDailySelection={showDailySelection}
            loadingMore={loadingMore}
            recipesPerPage={RECIPES_PER_PAGE}
            dailySelectionLabel={DAILY_SELECTION_LABEL}
            favoritePendingIds={favoritePendingIds}
            isFavorite={isFavorite}
            onFavoriteToggle={(recipe) => {
              void handleFavoriteToggle(recipe);
            }}
            lastRecipeElementRef={lastRecipeElementRef}
          />
        </ListGroup>
      )}
    </Flex>
  );
};

export default Home;
